import { supabase } from '../lib/supabase';

export interface Product {
  id: string;
  productName: string;
  groupName: string;
  unitPrice: number;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductGroup {
  id: string;
  name: string;
}

// Database column names (snake_case) to TypeScript property names (camelCase) mapping
const mapDbToProduct = (dbProduct: any): Product => ({
  id: dbProduct.id,
  productName: dbProduct.product_name,
  groupName: dbProduct.group_name,
  unitPrice: Number(dbProduct.unit_price),
  description: dbProduct.description,
  createdAt: dbProduct.created_at,
  updatedAt: dbProduct.updated_at,
});

// TypeScript to Database mapping
const mapProductToDb = (product: Partial<Product>) => ({
  product_name: product.productName,
  group_name: product.groupName,
  unit_price: product.unitPrice,
  description: product.description,
});

/**
 * Fetch all products from Supabase
 */
export const getAllProducts = async (): Promise<Product[]> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('group_name', { ascending: true })
      .order('product_name', { ascending: true });

    if (error) throw error;

    return data?.map(mapDbToProduct) || [];
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Get products by group name
 */
export const getProductsByGroup = async (groupName: string): Promise<Product[]> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('group_name', groupName)
      .order('product_name', { ascending: true });

    if (error) throw error;

    return data?.map(mapDbToProduct) || [];
  } catch (error) {
    console.error(`Error fetching products for group ${groupName}:`, error);
    throw error;
  }
};

/**
 * Get license products (Creatio Product, Creatio Studio, Evoqia Product License)
 */
export const getLicenseProducts = async (): Promise<Product[]> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .in('group_name', [
        'Creatio Product License',
        'Creatio Studio License',
        'Evoqia Product License',
      ])
      .order('group_name', { ascending: true })
      .order('product_name', { ascending: true });

    if (error) throw error;

    return data?.map(mapDbToProduct) || [];
  } catch (error) {
    console.error('Error fetching license products:', error);
    throw error;
  }
};

/**
 * Get implementation products (Business Analysis, Development, Integrations, Portal)
 */
export const getImplementationProducts = async (): Promise<Product[]> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .in('group_name', [
        'Business Analysis',
        'Creatio Development',
        'Integrations',
        'Portal Development',
      ])
      .order('group_name', { ascending: true })
      .order('product_name', { ascending: true });

    if (error) throw error;

    return data?.map(mapDbToProduct) || [];
  } catch (error) {
    console.error('Error fetching implementation products:', error);
    throw error;
  }
};

/**
 * Create a new product
 */
export const createProduct = async (product: Omit<Product, 'id'>): Promise<Product> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .insert([mapProductToDb(product)])
      .select()
      .single();

    if (error) throw error;

    return mapDbToProduct(data);
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

/**
 * Update an existing product
 */
export const updateProduct = async (
  id: string,
  updates: Partial<Product>
): Promise<Product> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .update(mapProductToDb(updates))
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return mapDbToProduct(data);
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

/**
 * Delete a product
 */
export const deleteProduct = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase.from('products').delete().eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

/**
 * Get unique product groups
 */
export const getProductGroups = async (): Promise<ProductGroup[]> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('group_name')
      .order('group_name', { ascending: true });

    if (error) throw error;

    // Get unique group names
    const uniqueGroups = [...new Set(data?.map((p) => p.group_name) || [])];

    return uniqueGroups.map((name, index) => ({
      id: `group-${index}`,
      name,
    }));
  } catch (error) {
    console.error('Error fetching product groups:', error);
    throw error;
  }
};
function productService(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: productService is not implemented yet.', args);
  return null;
}

export default productService;